import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from './PageSEO';

const LAST_UPDATED = 'June 16, 2026';

const SECTIONS = [
  {
    heading: '1. Who We Are',
    body: [
      'WordZingo ("we", "us", or "our") operates the website wordzingo.com — a free collection of word games, puzzle solvers, and word tools. This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using wordzingo.com you agree to the practices described here.',
    ],
  },
  {
    heading: '2. Information We Collect',
    body: [
      'We aim to collect as little information as possible. The information we handle falls into three categories:',
    ],
    list: [
      ['Usage & analytics data', 'When you visit the site we automatically receive standard technical information such as your browser type, device type, approximate location (country/region), pages visited, and how you arrived at the site. This is collected through Google Analytics (see Section 4).'],
      ['Data stored on your device', 'Some features — like Wordle streaks, saved words in the Random Word Generator, or game progress — are stored locally in your browser using localStorage. This data stays on your device and is never sent to us.'],
      ['Words and puzzle inputs', 'When you use a solver, the letters or words you enter may be sent to our backend to look up valid words. We use these inputs only to return your results and do not link them to you or store them to build a profile.'],
    ],
  },
  {
    heading: '3. How We Use Information',
    body: [
      'We use the limited information we collect to operate and improve WordZingo — specifically to: keep the site running and secure, understand which games are popular so we can improve them, fix bugs and measure performance, and return the results you request from our solvers. We do not sell your personal information.',
    ],
  },
  {
    heading: '4. Cookies, Analytics & Advertising',
    body: [
      'WordZingo uses Google Analytics, a web analytics service provided by Google, to understand how visitors use the site. Google Analytics uses cookies and similar technologies to collect aggregated, anonymized usage statistics (such as page views and session duration). This helps us see what works and what to improve.',
      'WordZingo also displays advertising through Google AdSense. Google and its partners use cookies and similar technologies to serve ads based on your prior visits to this and other websites. Google’s use of advertising cookies enables it and its partners to serve ads to you based on your visit to WordZingo and/or other sites on the Internet.',
      'You may opt out of personalized advertising by visiting Google’s Ads Settings at adssettings.google.com, and you can opt out of a third-party vendor’s use of cookies for personalized advertising by visiting aboutads.info. You can also opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on, or by blocking cookies in your browser settings. Most browsers also let you delete existing cookies and refuse new ones. Learn more in Google’s Privacy Policy at policies.google.com/privacy.',
    ],
  },
  {
    heading: '5. Third-Party Services',
    body: [
      'We rely on a small number of trusted third parties to deliver the site, including Google Analytics (usage measurement), Google AdSense (advertising), Google Fonts and a content delivery network (for fonts and icons), and our hosting provider. These providers may process limited technical data (such as your IP address) as needed to deliver their service. We do not control, and are not responsible for, the privacy practices of third-party websites you may reach through links on our site.',
    ],
  },
  {
    heading: '6. Children’s Privacy',
    body: [
      'WordZingo is family-friendly and suitable for all ages, but it is not directed at children under 13, and we do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it.',
    ],
  },
  {
    heading: '7. Your Choices & Rights',
    body: [
      'You can clear locally stored game data at any time by clearing your browser’s storage for wordzingo.com. Depending on where you live, you may have rights to access, correct, or delete personal data we hold about you — to exercise any such right, contact us using the details below.',
    ],
  },
  {
    heading: '8. Data Security & Retention',
    body: [
      'We take reasonable measures to protect the information we process, and we keep analytics data only for as long as needed to understand site usage. No method of transmission over the internet is 100% secure, so while we strive to protect your information we cannot guarantee absolute security.',
    ],
  },
  {
    heading: '9. Changes to This Policy',
    body: [
      'We may update this Privacy Policy from time to time to reflect changes to our practices or for legal reasons. When we do, we will revise the "Last updated" date at the top of this page. We encourage you to review this page periodically.',
    ],
  },
  {
    heading: '10. Contact Us',
    body: [
      'If you have any questions about this Privacy Policy or how we handle your information, please reach out to us through the contact options on wordzingo.com.',
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div>
      <PageSEO page="privacy" />

      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)',
        color: '#fff',
        padding: '4rem 1rem 3rem',
        textAlign: 'center',
      }}>
        <div className="container" style={{ maxWidth: 680 }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🔒</div>
          <h1 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(2rem, 5vw, 2.8rem)', marginBottom: '1rem', letterSpacing: '-0.02em' }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: '1.05rem', opacity: 0.88, lineHeight: 1.7, margin: 0 }}>
            Your privacy matters. Here’s exactly what WordZingo collects, why, and the
            choices you have — in plain English.
          </p>
          <p style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '1rem' }}>
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* ── Body ── */}
      <section style={{ padding: '3rem 1rem' }}>
        <div className="container" style={{ maxWidth: 760 }}>
          {SECTIONS.map(({ heading, body, list }) => (
            <div key={heading} style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: 'clamp(1.25rem, 3.5vw, 1.6rem)', color: '#1e293b', marginBottom: '0.75rem' }}>
                {heading}
              </h2>
              {body.map((p, i) => (
                <p key={i} style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.8, marginBottom: '0.85rem' }}>{p}</p>
              ))}
              {list && (
                <ul style={{ paddingLeft: '1.1rem', margin: '0.25rem 0 0.5rem' }}>
                  {list.map(([term, desc]) => (
                    <li key={term} style={{ fontSize: '1rem', color: '#374151', lineHeight: 1.8, marginBottom: '0.6rem' }}>
                      <strong style={{ color: '#1e293b' }}>{term}:</strong> {desc}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div style={{ borderTop: '2px solid var(--border)', paddingTop: '1.75rem', marginTop: '0.5rem', textAlign: 'center' }}>
            <Link to="/" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--primary)', color: '#fff', border: 'none', fontFamily: 'Nunito, sans-serif', padding: '0.65rem 2rem', fontSize: '1rem' }}>
              🎮 Back to Games
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
